import { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { addAccount, removeAccount } from '../../../utils/demoEnvMgmtApi';
import { errorMessage } from './error-message';
import ConfirmDeleteDialog from '../../../components/admin/ConfirmDeleteDialog';

function AccountsSection({ accounts, onChanged }) {
  const [label, setLabel] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await addAccount({ label: label.trim() });
      setLabel('');
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await removeAccount(deleteTarget.id);
      setDeleteTarget(null);
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
      setDeleteTarget(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="demo-env-mgmt-section">
      <h2 className="demo-env-mgmt-section-title">Accounts</h2>
      <p className="demo-env-mgmt-label">
        Demo CSQ tenants — the top of the hierarchy below. Can exist before any login is set up;
        each is linked 1-to-1 to a User (its login, added further down) once one exists, and has
        many Projects. Selected first on the Entities tab, ahead of any datasource.
      </p>

      <div className="demo-env-mgmt-columns" style={{ marginTop: 8 }}>
        <div className="demo-env-mgmt-column">
          <h3 className="demo-env-mgmt-column-title">Add account</h3>
          <form onSubmit={handleAdd}>
            <div className="demo-env-mgmt-field">
              <label className="demo-env-mgmt-label" htmlFor="new-account-label">Label</label>
              <input
                id="new-account-label"
                className="demo-env-mgmt-input"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
              />
            </div>
            <div className="demo-env-mgmt-field demo-env-mgmt-field-actions" style={{ marginTop: 8 }}>
              <button className="demo-env-mgmt-btn" type="submit" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add'}
              </button>
            </div>
          </form>
          {error && <p className="demo-env-mgmt-error">{error}</p>}
        </div>

        <div className="demo-env-mgmt-column">
          <h3 className="demo-env-mgmt-column-title">Accounts</h3>
          <div className="demo-env-mgmt-table-wrap">
            <table className="demo-env-mgmt-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Linked user</th>
                  <th className="demo-env-mgmt-table-actions" />
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => (
                  <tr key={account.id}>
                    <td>{account.label}</td>
                    <td>{account.user ? account.user.email : '—'}</td>
                    <td className="demo-env-mgmt-table-actions">
                      <button
                        type="button"
                        className="demo-env-mgmt-btn-delete"
                        onClick={() => setDeleteTarget(account)}
                        disabled={submitting}
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {accounts.length === 0 && (
                  <tr className="demo-env-mgmt-empty-row">
                    <td colSpan={3}>No accounts yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <ConfirmDeleteDialog
          entityType="account"
          entityName={deleteTarget.label}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

export default AccountsSection;
